<template>
  <section class="admin-shell">
    <header class="admin-hero">
      <div>
        <span class="admin-eyebrow">Admin Console</span>
        <h1>网站后台管理</h1>
        <p>
          账号、权限、导航、技术手记和光影音域现在都已经接到本地后端。内容编辑区改成了草稿模式，
          你在后台的修改只有点击“确认保存内容”后才会真正写入服务器并影响前台显示。
        </p>

        <p
          v-if="contentMessage || contentError || storeContentError"
          class="admin-inline-message"
          :class="{ 'is-error': contentMessageError || Boolean(storeContentError) }"
        >
          {{ contentMessage || storeContentError || contentError }}
        </p>
      </div>

      <div class="admin-hero-actions">
        <RouterLink class="admin-link-button" to="/">查看前台</RouterLink>
        <button
          type="button"
          class="admin-secondary-button"
          :disabled="!contentDirty || savingContent"
          @click="saveDraftContent"
        >
          {{ savingContent ? "正在保存..." : "确认保存内容" }}
        </button>
        <button
          type="button"
          class="admin-secondary-button"
          :disabled="!contentDirty || savingContent"
          @click="discardDraftChanges"
        >
          放弃未保存修改
        </button>
        <button
          type="button"
          class="admin-danger-button"
          :disabled="savingContent"
          @click="resetDraftToDefault"
        >
          恢复默认草稿
        </button>
      </div>
    </header>

    <section v-if="isSuperAdmin" class="admin-section admin-section-accounts">
      <div class="admin-section-head">
        <div>
          <span class="admin-section-eyebrow">Accounts</span>
          <h2>用户与权限管理</h2>
          <p class="admin-section-tip">
            这里创建的账号会直接存进后端，部署到云服务器后，其他电脑也会使用同一套账号体系。
          </p>
        </div>
        <button
          type="button"
          class="admin-secondary-button"
          :disabled="usersLoading"
          @click="loadUsers"
        >
          {{ usersLoading ? "正在刷新..." : "刷新用户列表" }}
        </button>
      </div>

      <div class="admin-grid admin-grid-users">
        <div class="admin-panel">
          <div class="admin-panel-head">
            <h3>新增用户</h3>
            <span>只有超级管理员可以创建账号和分配权限</span>
          </div>

          <div class="admin-form-grid">
            <label class="admin-field">
              <span>用户名</span>
              <input v-model.trim="newUser.username" type="text" placeholder="例如：editor01">
            </label>
            <label class="admin-field">
              <span>显示名称</span>
              <input v-model.trim="newUser.displayName" type="text" placeholder="例如：内容编辑">
            </label>
            <label class="admin-field admin-field-full">
              <span>初始密码</span>
              <input v-model="newUser.password" type="password" placeholder="至少 6 位">
            </label>
          </div>

          <div class="admin-check-row">
            <label class="admin-check">
              <input v-model="newUser.canAccessAdmin" type="checkbox">
              <span>允许进入后台</span>
            </label>
            <label class="admin-check">
              <input v-model="newUser.isSuperAdmin" type="checkbox">
              <span>设为超级管理员</span>
            </label>
          </div>

          <p
            v-if="userActionMessage"
            class="admin-inline-message"
            :class="{ 'is-error': userActionError }"
          >
            {{ userActionMessage }}
          </p>

          <button
            type="button"
            class="admin-secondary-button"
            :disabled="usersLoading"
            @click="createUser"
          >
            创建用户
          </button>
        </div>

        <div class="admin-panel">
          <div class="admin-panel-head">
            <h3>已有用户</h3>
            <span>{{ users.length }} 个账号</span>
          </div>

          <p v-if="!users.length && !usersLoading" class="admin-empty-tip">
            目前还没有更多用户。
          </p>

          <div v-for="user in users" :key="user.id" class="admin-card">
            <div class="admin-card-toolbar">
              <strong>{{ user.displayName || user.username }}</strong>
              <button
                v-if="user.id !== currentUser?.id"
                type="button"
                class="admin-text-button is-danger"
                @click="deleteUser(user.id)"
              >
                删除用户
              </button>
            </div>

            <div class="admin-form-grid">
              <label class="admin-field">
                <span>用户名</span>
                <input
                  v-model.trim="user.username"
                  type="text"
                  @change="saveUser(user, { username: user.username })"
                >
              </label>
              <label class="admin-field">
                <span>显示名称</span>
                <input
                  v-model.trim="user.displayName"
                  type="text"
                  @change="saveUser(user, { displayName: user.displayName })"
                >
              </label>
            </div>

            <div class="admin-check-row">
              <label class="admin-check">
                <input
                  :checked="user.canAccessAdmin"
                  type="checkbox"
                  @change="saveUser(user, { canAccessAdmin: $event.target.checked })"
                >
                <span>允许进入后台</span>
              </label>
              <label class="admin-check">
                <input
                  :checked="user.isSuperAdmin"
                  type="checkbox"
                  @change="saveUser(user, { isSuperAdmin: $event.target.checked })"
                >
                <span>超级管理员</span>
              </label>
            </div>

            <p class="admin-empty-tip">
              当前用户名：{{ user.username }}
              <span v-if="currentUser?.id === user.id">（你自己）</span>
            </p>
          </div>
        </div>
      </div>
    </section>

    <section class="admin-section admin-section-status">
      <div class="admin-section-head">
        <div>
          <span class="admin-section-eyebrow">Content Draft</span>
          <h2>内容草稿状态</h2>
          <p class="admin-section-tip">
            当前后台编辑的是草稿。只有点击顶部“确认保存内容”，导航、技术手记和光影音域才会同步到前台。
          </p>
        </div>
        <div class="admin-actions-row">
          <button type="button" class="admin-secondary-button" @click="reloadDraftFromServer">
            从服务器重新载入
          </button>
          <span class="admin-status-pill" :class="{ 'is-dirty': contentDirty }">
            {{ contentDirty ? "有未保存修改" : "当前草稿已同步" }}
          </span>
        </div>
      </div>
    </section>

    <section class="admin-section admin-section-module-intro">
      <div class="admin-section-head">
        <div>
          <span class="admin-section-eyebrow">Module Intro</span>
          <h2>前台板块介绍草稿</h2>
          <p class="admin-section-tip">
            这里控制技术手记和光影音域进入后的顶部介绍文案。修改后同样需要点击“确认保存内容”才会同步到前台。
          </p>
        </div>
      </div>

      <div class="admin-grid admin-grid-nav">
        <div
          v-for="moduleItem in moduleIntroEditors"
          :key="moduleItem.key"
          class="admin-panel"
        >
          <div class="admin-panel-head">
            <h3>{{ moduleItem.label }}</h3>
            <span>{{ moduleItem.routeLabel }}</span>
          </div>

          <div class="admin-form-grid">
            <label class="admin-field admin-field-full">
              <span>眉标</span>
              <input
                v-model="draftModuleIntros[moduleItem.key].eyebrow"
                type="text"
                @input="patchModuleIntro(moduleItem.key)"
              >
            </label>
            <label class="admin-field admin-field-full">
              <span>标题</span>
              <textarea
                v-model="draftModuleIntros[moduleItem.key].title"
                rows="2"
                @input="patchModuleIntro(moduleItem.key)"
              ></textarea>
            </label>
            <label class="admin-field admin-field-full">
              <span>说明文字</span>
              <textarea
                v-model="draftModuleIntros[moduleItem.key].description"
                rows="4"
                @input="patchModuleIntro(moduleItem.key)"
              ></textarea>
            </label>
            <label class="admin-field admin-field-full">
              <span>标签</span>
              <small>用中文逗号、英文逗号或换行分隔，前台会显示为一组小标签。</small>
              <textarea
                :value="draftModuleIntros[moduleItem.key].tags.join('，')"
                rows="2"
                @input="patchModuleIntroTags(moduleItem.key, $event.target.value)"
              ></textarea>
            </label>
          </div>
        </div>
      </div>
    </section>

    <section class="admin-section admin-section-light-videos">
      <div class="admin-section-head">
        <div>
          <span class="admin-section-eyebrow">Light Videos</span>
          <h2>光影音域草稿</h2>
          <p class="admin-section-tip">
            这里用于管理你做过的视频作品。填写 B 站视频详情页地址后，前台播放页会自动生成内嵌播放器。
          </p>
        </div>
        <button type="button" class="admin-secondary-button" @click="createVideo">
          新增光影音域视频
        </button>
      </div>

      <div class="admin-grid admin-grid-notes">
        <aside class="admin-panel admin-note-list">
          <div class="admin-panel-head">
            <h3>草稿视频</h3>
            <span>{{ filteredDraftLightVideos.length }} / {{ draftLightVideos.length }} 个</span>
          </div>

          <label class="admin-list-search">
            <span>搜索视频</span>
            <input
              v-model.trim="lightVideoSearchTerm"
              type="search"
              placeholder="按标题、分类、摘要或 BV 号搜索"
            >
          </label>

          <div class="admin-note-scroll">
            <button
              v-for="video in filteredDraftLightVideos"
              :key="video.id"
              type="button"
              class="admin-note-item"
              :class="{ 'is-active': video.id === selectedVideoId }"
              @click="selectedVideoId = video.id"
            >
              <strong>{{ video.title }}</strong>
              <span>{{ video.categoryTitle }} · {{ video.date }}</span>
            </button>

            <p v-if="draftLightVideos.length && !filteredDraftLightVideos.length" class="admin-empty-tip">
              没有找到匹配的视频。
            </p>
          </div>

          <p v-if="!draftLightVideos.length" class="admin-empty-tip">
            还没有视频草稿，点击右上角新增即可。
          </p>
        </aside>

        <div class="admin-panel admin-note-editor">
          <div v-if="selectedVideo" class="admin-note-form">
            <div class="admin-card-toolbar">
              <div>
                <strong>编辑光影音域视频</strong>
                <p class="admin-card-tip">这里的修改先只停留在草稿里，保存后才会发布到前台。</p>
              </div>
              <button
                type="button"
                class="admin-text-button is-danger"
                @click="removeVideo(selectedVideo.id)"
              >
                删除视频
              </button>
            </div>

            <div class="admin-form-grid admin-form-grid-note">
              <label class="admin-field">
                <span>视频标题</span>
                <input
                  v-model="selectedVideo.title"
                  type="text"
                  placeholder="例如：某次作品成片记录"
                  @input="patchVideo(selectedVideo.id, { title: selectedVideo.title })"
                >
              </label>

              <label class="admin-field admin-field-full">
                <span>B 站视频链接</span>
                <small>支持包含 BV 号或 av 号的 B 站详情页地址。b23.tv 短链需要先展开成正式链接再填写。</small>
                <input
                  v-model="selectedVideo.sourceUrl"
                  type="url"
                  placeholder="https://www.bilibili.com/video/BV..."
                  @input="patchVideo(selectedVideo.id, { sourceUrl: selectedVideo.sourceUrl })"
                >
              </label>

              <label class="admin-field">
                <span>时长 / 标签</span>
                <input
                  v-model="selectedVideo.duration"
                  type="text"
                  placeholder="例如：03:26 或 视频作品"
                  @input="patchVideo(selectedVideo.id, { duration: selectedVideo.duration })"
                >
              </label>

              <label class="admin-field">
                <span>分类名称</span>
                <input
                  v-model="selectedVideo.categoryTitle"
                  type="text"
                  placeholder="例如：短片练习"
                  @input="patchVideo(selectedVideo.id, { categoryTitle: selectedVideo.categoryTitle })"
                >
              </label>

              <label class="admin-field">
                <span>日期</span>
                <input
                  v-model="selectedVideo.date"
                  type="date"
                  @input="patchVideo(selectedVideo.id, { date: selectedVideo.date })"
                >
              </label>

              <label class="admin-field admin-field-full">
                <span>视频简介</span>
                <textarea
                  v-model="selectedVideo.heroSummary"
                  rows="3"
                  @input="patchVideo(selectedVideo.id, { heroSummary: selectedVideo.heroSummary })"
                ></textarea>
              </label>
            </div>

            <div class="admin-video-preview">
              <div class="admin-panel-head">
                <h3>内嵌播放预览</h3>
                <RouterLink
                  v-if="selectedVideo.slug"
                  :to="`/light-videos/${selectedVideo.slug}`"
                >
                  查看前台播放页
                </RouterLink>
              </div>

              <div v-if="selectedVideoEmbedUrl" class="admin-video-preview__frame">
                <iframe
                  :src="selectedVideoEmbedUrl"
                  :title="selectedVideo.title"
                  loading="lazy"
                  allow="fullscreen; autoplay; encrypted-media; picture-in-picture"
                  allowfullscreen
                ></iframe>
              </div>

              <p v-else class="admin-empty-tip">
                填入包含 BV 号或 av 号的 B 站链接后，这里会显示内嵌播放器预览。
              </p>
            </div>
          </div>

          <div v-else class="notes-empty-state">
            <h2>还没有选中视频</h2>
            <p>先从左侧选择一个视频，或者点击“新增光影音域视频”。</p>
          </div>
        </div>
      </div>
    </section>

    <section class="admin-section admin-section-sound-blank">
      <div class="admin-section-head">
        <div>
          <span class="admin-section-eyebrow">Sound Blank</span>
          <h2>声音留白草稿</h2>
          <p class="admin-section-tip">
            这里用于管理歌曲播放块。填写音频链接地址并保存后，前台会直接生成可点击播放的歌曲模块。
          </p>
        </div>
        <button type="button" class="admin-secondary-button" @click="createAudioTrack">
          新增声音留白歌曲
        </button>
      </div>

      <div class="admin-grid admin-grid-notes">
        <aside class="admin-panel admin-note-list">
          <div class="admin-panel-head">
            <h3>草稿歌曲</h3>
            <span>{{ filteredDraftAudioTracks.length }} / {{ draftAudioTracks.length }} 首</span>
          </div>

          <label class="admin-list-search">
            <span>搜索歌曲</span>
            <input
              v-model.trim="audioTrackSearchTerm"
              type="search"
              placeholder="按歌名、作者、分类或链接搜索"
            >
          </label>

          <div class="admin-note-scroll">
            <button
              v-for="track in filteredDraftAudioTracks"
              :key="track.id"
              type="button"
              class="admin-note-item"
              :class="{ 'is-active': track.id === selectedAudioTrackId }"
              @click="selectedAudioTrackId = track.id"
            >
              <strong>{{ track.title }}</strong>
              <span>{{ track.artist || track.categoryTitle }} · {{ track.date }}</span>
            </button>

            <p v-if="draftAudioTracks.length && !filteredDraftAudioTracks.length" class="admin-empty-tip">
              没有找到匹配的歌曲。
            </p>
          </div>

          <p v-if="!draftAudioTracks.length" class="admin-empty-tip">
            还没有歌曲草稿，点击右上角新增即可。
          </p>
        </aside>

        <div class="admin-panel admin-note-editor">
          <div v-if="selectedAudioTrack" class="admin-note-form">
            <div class="admin-card-toolbar">
              <div>
                <strong>编辑声音留白歌曲</strong>
                <p class="admin-card-tip">这里的修改先停留在草稿里，保存后才会发布到前台。</p>
              </div>
              <button
                type="button"
                class="admin-text-button is-danger"
                @click="removeAudioTrack(selectedAudioTrack.id)"
              >
                删除歌曲
              </button>
            </div>

            <div class="admin-form-grid admin-form-grid-note">
              <label class="admin-field">
                <span>歌曲名称</span>
                <input
                  v-model="selectedAudioTrack.title"
                  type="text"
                  placeholder="例如：永远不回头"
                  @input="patchAudioTrack(selectedAudioTrack.id, { title: selectedAudioTrack.title })"
                >
              </label>

              <label class="admin-field">
                <span>作者 / 歌手</span>
                <input
                  v-model="selectedAudioTrack.artist"
                  type="text"
                  placeholder="例如：特瑞饭"
                  @input="patchAudioTrack(selectedAudioTrack.id, { artist: selectedAudioTrack.artist })"
                >
              </label>

              <label class="admin-field admin-field-full">
                <span>音频链接地址</span>
                <small>填写 m4a、mp3、wav 等浏览器可播放的直链地址。</small>
                <input
                  v-model="selectedAudioTrack.audioUrl"
                  type="url"
                  placeholder="https://example.com/song.m4a"
                  @input="patchAudioTrack(selectedAudioTrack.id, { audioUrl: selectedAudioTrack.audioUrl })"
                >
              </label>

              <label class="admin-field admin-field-full">
                <span>歌词文件</span>
                <small>支持上传 .lrc 或 .txt 文件；LRC 时间轴格式会在播放时逐句同步显示。</small>
                <input
                  v-model="selectedAudioTrack.lyricsUrl"
                  type="text"
                  placeholder="/uploads/lyrics/song.lrc"
                  @input="patchAudioTrack(selectedAudioTrack.id, { lyricsUrl: selectedAudioTrack.lyricsUrl })"
                >
              </label>

              <div class="admin-field admin-field-full admin-audio-lyrics-actions">
                <label class="admin-upload-button">
                  上传歌词文件
                  <input
                    type="file"
                    accept=".lrc,.txt,text/plain"
                    @change="handleAudioLyricsUpload"
                  >
                </label>
                <button
                  type="button"
                  class="admin-danger-button"
                  :disabled="!selectedAudioTrack.lyricsUrl"
                  @click="clearAudioLyrics"
                >
                  清除歌词
                </button>
              </div>

              <label class="admin-field">
                <span>时长 / 标签</span>
                <input
                  v-model="selectedAudioTrack.duration"
                  type="text"
                  placeholder="例如：03:45 或 音频作品"
                  @input="patchAudioTrack(selectedAudioTrack.id, { duration: selectedAudioTrack.duration })"
                >
              </label>

              <label class="admin-field">
                <span>分类名称</span>
                <input
                  v-model="selectedAudioTrack.categoryTitle"
                  type="text"
                  placeholder="例如：音乐"
                  @input="patchAudioTrack(selectedAudioTrack.id, { categoryTitle: selectedAudioTrack.categoryTitle })"
                >
              </label>

              <label class="admin-field">
                <span>日期</span>
                <input
                  v-model="selectedAudioTrack.date"
                  type="date"
                  @input="patchAudioTrack(selectedAudioTrack.id, { date: selectedAudioTrack.date })"
                >
              </label>

              <label class="admin-field admin-field-full">
                <span>歌曲简介</span>
                <textarea
                  v-model="selectedAudioTrack.summary"
                  rows="3"
                  @input="patchAudioTrack(selectedAudioTrack.id, { summary: selectedAudioTrack.summary })"
                ></textarea>
              </label>
            </div>

            <div class="admin-video-preview">
              <div class="admin-panel-head">
                <h3>音频预览</h3>
                <RouterLink to="/sound-blank">
                  查看前台声音留白
                </RouterLink>
              </div>

              <audio
                v-if="selectedAudioTrack.audioUrl"
                class="admin-audio-preview"
                :src="selectedAudioPreviewUrl"
                controls
                preload="metadata"
              ></audio>

              <p v-else class="admin-empty-tip">
                填入音频链接后，这里会显示本地预览播放器。
              </p>

              <p v-if="selectedAudioTrack.lyricsUrl" class="admin-empty-tip">
                已关联歌词：{{ selectedAudioTrack.lyricsUrl }}
              </p>
            </div>
          </div>

          <div v-else class="notes-empty-state">
            <h2>还没有选中歌曲</h2>
            <p>先从左侧选择一首歌曲，或者点击“新增声音留白歌曲”。</p>
          </div>
        </div>
      </div>
    </section>

    <section class="admin-section admin-section-common-software">
      <div class="admin-section-head">
        <div>
          <span class="admin-section-eyebrow">Common Software</span>
          <h2>常用软件草稿</h2>
          <p class="admin-section-tip">
            这里用于管理常用软件下载入口。上传 Logo、填写名称和网址并保存后，前台会自动生成软件卡片。
          </p>
        </div>
        <button type="button" class="admin-secondary-button" @click="createSoftwareLink">
          新增常用软件
        </button>
      </div>

      <div class="admin-grid admin-grid-notes">
        <aside class="admin-panel admin-note-list">
          <div class="admin-panel-head">
            <h3>草稿软件</h3>
            <span>{{ filteredDraftSoftwareLinks.length }} / {{ draftSoftwareLinks.length }} 个</span>
          </div>

          <label class="admin-list-search">
            <span>搜索软件</span>
            <input
              v-model.trim="softwareSearchTerm"
              type="search"
              placeholder="按名称、网址或 Logo 地址搜索"
            >
          </label>

          <div class="admin-note-scroll">
            <button
              v-for="software in filteredDraftSoftwareLinks"
              :key="software.id"
              type="button"
              class="admin-note-item"
              :class="{ 'is-active': software.id === selectedSoftwareLinkId }"
              @click="selectedSoftwareLinkId = software.id"
            >
              <strong>{{ software.name }}</strong>
              <span>{{ software.url || "等待填写网址" }}</span>
            </button>

            <p v-if="draftSoftwareLinks.length && !filteredDraftSoftwareLinks.length" class="admin-empty-tip">
              没有找到匹配的软件。
            </p>
          </div>

          <p v-if="!draftSoftwareLinks.length" class="admin-empty-tip">
            还没有软件草稿，点击右上角新增即可。
          </p>
        </aside>

        <div class="admin-panel admin-note-editor">
          <div v-if="selectedSoftwareLink" class="admin-note-form">
            <div class="admin-card-toolbar">
              <div>
                <strong>编辑常用软件</strong>
                <p class="admin-card-tip">这里的修改先停留在草稿里，保存后才会发布到前台。</p>
              </div>
              <button
                type="button"
                class="admin-text-button is-danger"
                @click="removeSoftwareLink(selectedSoftwareLink.id)"
              >
                删除软件
              </button>
            </div>

            <div class="admin-form-grid admin-form-grid-note">
              <label class="admin-field">
                <span>软件名称</span>
                <input
                  v-model="selectedSoftwareLink.name"
                  type="text"
                  placeholder="例如：WPS Office"
                  @input="patchSoftwareLink(selectedSoftwareLink.id, { name: selectedSoftwareLink.name })"
                >
              </label>

              <label class="admin-field">
                <span>下载 / 官网网址</span>
                <input
                  v-model="selectedSoftwareLink.url"
                  type="url"
                  placeholder="https://example.com"
                  @input="patchSoftwareLink(selectedSoftwareLink.id, { url: selectedSoftwareLink.url })"
                >
              </label>

              <label class="admin-check admin-field-full">
                <input
                  v-model="selectedSoftwareLink.isPublic"
                  type="checkbox"
                  @change="patchSoftwareLink(selectedSoftwareLink.id, { isPublic: selectedSoftwareLink.isPublic })"
                >
                <span>允许未登录用户在前台点击访问</span>
              </label>

              <label class="admin-field admin-field-full">
                <span>Logo 地址</span>
                <small>可以粘贴图片地址，也可以用下面的按钮上传本地图片。</small>
                <input
                  v-model="selectedSoftwareLink.logo"
                  type="text"
                  placeholder="/uploads/images/software-logo.webp"
                  @input="patchSoftwareLink(selectedSoftwareLink.id, { logo: selectedSoftwareLink.logo })"
                >
              </label>

              <div class="admin-field admin-field-full admin-audio-lyrics-actions">
                <button
                  type="button"
                  class="admin-upload-button"
                  @click="triggerSoftwareLogoUpload"
                >
                  上传软件 Logo
                </button>
                <input
                  ref="softwareLogoInputRef"
                  class="admin-hidden-file-input"
                  type="file"
                  accept="image/*"
                  @change="handleSoftwareLogoUpload"
                >
                <button
                  type="button"
                  class="admin-danger-button"
                  :disabled="!selectedSoftwareLink.logo"
                  @click="clearSoftwareLogo"
                >
                  清除 Logo
                </button>
              </div>

              <p class="admin-preview-meta">
                当前 Logo：{{ selectedSoftwareLink.logo || "未上传" }}
              </p>
            </div>

            <div class="admin-video-preview">
              <div class="admin-panel-head">
                <h3>卡片预览</h3>
                <RouterLink to="/common-software">
                  查看前台常用软件
                </RouterLink>
              </div>

              <a
                class="admin-software-preview"
                :href="selectedSoftwareLink.url || '#'"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span class="admin-software-logo-preview">
                  <img
                    v-if="selectedSoftwareLink.logo"
                    :src="selectedSoftwareLink.logo"
                    :alt="selectedSoftwareLink.name"
                  >
                  <span v-else>{{ selectedSoftwareLink.name.slice(0, 1) || "软" }}</span>
                </span>
                <strong>{{ selectedSoftwareLink.name }}</strong>
                <small>{{ selectedSoftwareLink.isPublic ? "未登录可访问" : "需要登录后访问" }}</small>
              </a>
            </div>
          </div>

          <div v-else class="notes-empty-state">
            <h2>还没有选中软件</h2>
            <p>先从左侧选择一个软件，或者点击“新增常用软件”。</p>
          </div>
        </div>
      </div>
    </section>

    <section class="admin-section admin-section-navigation">
      <div class="admin-section-head">
        <div>
          <span class="admin-section-eyebrow">Navigation</span>
          <h2>导航下拉菜单草稿</h2>
          <p class="admin-section-tip">
            这里的调整会先停留在草稿区，保存后才会真正影响前台导航。
          </p>
        </div>
        <div class="admin-actions-row">
          <button type="button" class="admin-secondary-button" @click="addGroup('left')">
            新增左侧导航组
          </button>
          <button type="button" class="admin-secondary-button" @click="addGroup('right')">
            新增右侧导航组
          </button>
        </div>
      </div>

      <div class="admin-grid admin-grid-nav">
        <div v-for="side in navSides" :key="side.key" class="admin-panel">
          <div class="admin-panel-head">
            <h3>{{ side.label }}</h3>
            <span>{{ draftNavGroups[side.key].length }} 组</span>
          </div>

          <div v-for="group in draftNavGroups[side.key]" :key="group.id" class="admin-card">
            <div class="admin-card-toolbar">
              <strong>导航组</strong>
              <button
                type="button"
                class="admin-text-button is-danger"
                @click="removeGroup(side.key, group.id)"
              >
                删除组
              </button>
            </div>

            <div class="admin-form-grid">
              <label class="admin-field">
                <span>完整标题</span>
                <input
                  v-model="group.full"
                  type="text"
                  placeholder="例如：信息管理"
                  @input="updateDraftNavGroup(side.key, group.id, { full: group.full })"
                >
              </label>
              <label class="admin-field">
                <span>短标题</span>
                <input
                  v-model="group.short"
                  type="text"
                  placeholder="例如：信息"
                  @input="updateDraftNavGroup(side.key, group.id, { short: group.short })"
                >
              </label>
            </div>

            <div class="admin-subsection">
              <div class="admin-card-toolbar">
                <strong>下拉链接</strong>
                <button
                  type="button"
                  class="admin-text-button"
                  @click="addLink(side.key, group.id)"
                >
                  新增链接
                </button>
              </div>

              <div
                v-for="(link, linkIndex) in group.links"
                :key="link.id"
                class="admin-link-row"
              >
                <input
                  v-model="link.text"
                  type="text"
                  placeholder="链接名称"
                  @input="updateDraftNavLink(side.key, group.id, link.id, { text: link.text })"
                >
                <input
                  v-model="link.href"
                  type="url"
                  placeholder="https://example.com"
                  @input="updateDraftNavLink(side.key, group.id, link.id, { href: link.href })"
                >
                <div class="admin-link-order-actions">
                  <button
                    type="button"
                    class="admin-inline-button"
                    :disabled="linkIndex === 0"
                    @click="moveNavLink(side.key, group.id, link.id, -1)"
                  >
                    上移
                  </button>
                  <button
                    type="button"
                    class="admin-inline-button"
                    :disabled="linkIndex === group.links.length - 1"
                    @click="moveNavLink(side.key, group.id, link.id, 1)"
                  >
                    下移
                  </button>
                </div>
                <button
                  type="button"
                  class="admin-inline-danger"
                  @click="removeLink(side.key, group.id, link.id)"
                >
                  删除
                </button>
              </div>

              <p v-if="!group.links.length" class="admin-empty-tip">
                这个导航组还没有链接，点“新增链接”即可添加。
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="admin-section admin-section-tech-notes">
      <div class="admin-section-head">
        <div>
          <span class="admin-section-eyebrow">Tech Notes</span>
          <h2>技术手记草稿</h2>
          <p class="admin-section-tip">
            文章编辑区现在也是草稿模式。你可以大胆修改，确认无误后再统一保存上线。
          </p>
        </div>
        <button type="button" class="admin-secondary-button" @click="createNote">
          新增技术手记
        </button>
      </div>

      <div class="admin-grid admin-grid-notes">
        <aside class="admin-panel admin-note-list">
          <div class="admin-panel-head">
            <h3>草稿文章</h3>
            <span>{{ filteredDraftTechNotes.length }} / {{ draftTechNotes.length }} 篇</span>
          </div>

          <label class="admin-list-search">
            <span>搜索文章</span>
            <input
              v-model.trim="techNoteSearchTerm"
              type="search"
              placeholder="按标题、分类、摘要或日期搜索"
            >
          </label>

          <div class="admin-note-scroll">
            <button
              v-for="note in filteredDraftTechNotes"
              :key="note.id"
              type="button"
              class="admin-note-item"
              :class="{ 'is-active': note.id === selectedNoteId }"
              @click="selectedNoteId = note.id"
            >
              <strong>{{ note.title }}</strong>
              <span>{{ note.categoryTitle }} · {{ note.date }}</span>
            </button>

            <p v-if="draftTechNotes.length && !filteredDraftTechNotes.length" class="admin-empty-tip">
              没有找到匹配的文章。
            </p>
          </div>
        </aside>

        <div class="admin-panel admin-note-editor">
          <div v-if="selectedNote" class="admin-editor-entry-card">
            <div class="admin-editor-entry-card__copy">
              <span class="admin-section-eyebrow">Editor Link</span>
              <h3>{{ selectedNote.title }}</h3>
              <p>
                正文可视化编辑器已经移到独立页面。这样图片、视频、代码块和实时预览都能用更宽的空间展示，不会再挤在后台总览页里。
              </p>
            </div>

            <div class="admin-editor-entry-card__actions">
              <RouterLink
                class="admin-secondary-button"
                :to="`/admin/tech-notes/${selectedNote.id}/editor`"
              >
                进入独立编辑页
              </RouterLink>
              <RouterLink
                class="admin-link-button"
                :to="`/tech-notes/${selectedNote.slug}`"
              >
                查看前台文章
              </RouterLink>
            </div>
          </div>

          <div v-if="false && selectedNote" class="admin-note-form">
            <div class="admin-card-toolbar">
              <div>
                <strong>编辑技术手记草稿</strong>
                <p class="admin-card-tip">这里的修改先只停留在草稿里，保存后才会发布到前台。</p>
              </div>
              <button
                type="button"
                class="admin-text-button is-danger"
                @click="removeNote(selectedNote.id)"
              >
                删除文章
              </button>
            </div>

            <div class="admin-form-grid admin-form-grid-note">
              <label class="admin-field">
                <span>标题</span>
                <small>前台列表页和详情页都会显示这个标题。</small>
                <input
                  v-model="selectedNote.title"
                  type="text"
                  placeholder="例如：Vue 后台系统搭建记录"
                  @input="patchNote(selectedNote.id, { title: selectedNote.title })"
                >
              </label>

              <label class="admin-field">
                <span>分类名称</span>
                <small>前台筛选标签会显示这个名字。</small>
                <input
                  v-model="selectedNote.categoryTitle"
                  type="text"
                  placeholder="例如：前端开发"
                  @input="patchNote(selectedNote.id, { categoryTitle: selectedNote.categoryTitle })"
                >
              </label>

              <label class="admin-field">
                <span>日期</span>
                <small>文章发布时间，会显示在前台列表和详情页。</small>
                <input
                  v-model="selectedNote.date"
                  type="date"
                  @input="patchNote(selectedNote.id, { date: selectedNote.date })"
                >
              </label>

              <label class="admin-field">
                <span>阅读时间</span>
                <small>例如“阅读时间 6 分钟”。</small>
                <input
                  v-model="selectedNote.reading"
                  type="text"
                  placeholder="阅读时间 6 分钟"
                  @input="patchNote(selectedNote.id, { reading: selectedNote.reading })"
                >
              </label>
            </div>

            <div class="admin-cover-editor">
              <div class="admin-cover-head">
                <div>
                  <h3>封面图片</h3>
                  <p>建议比例 `16:9`，推荐尺寸 `1200 × 675` 或更高，单张尽量控制在 `500KB` 左右。</p>
                </div>
                <div class="admin-cover-actions">
                  <label class="admin-upload-button">
                    <input type="file" accept="image/*" @change="handleCoverUpload">
                    上传封面
                  </label>
                  <button type="button" class="admin-inline-danger" @click="resetCoverImage">
                    恢复默认图
                  </button>
                </div>
              </div>

              <div class="admin-cover-grid">
                <div class="admin-cover-preview">
                  <img :src="selectedNote.image" :alt="selectedNote.title">
                </div>

                <div class="admin-cover-side">
                  <label class="admin-field">
                    <span>远程图片地址</span>
                    <small>如果不上传本地图片，也可以直接填写线上图片地址。</small>
                    <input
                      v-model="selectedNote.image"
                      type="text"
                      placeholder="https://example.com/cover.jpg"
                      @input="patchNote(selectedNote.id, { image: selectedNote.image })"
                    >
                  </label>
                </div>
              </div>
            </div>

            <label class="admin-field">
              <span>列表摘要</span>
              <small>显示在技术手记列表页，适合用 1 到 2 句话概括文章重点。</small>
              <textarea
                v-model="selectedNote.summary"
                rows="3"
                @input="patchNote(selectedNote.id, { summary: selectedNote.summary })"
              ></textarea>
            </label>

            <label class="admin-field">
              <span>顶部说明</span>
              <small>显示在文章详情页标题下方，适合写更完整一点的导语。</small>
              <textarea
                v-model="selectedNote.heroSummary"
                rows="3"
                @input="patchNote(selectedNote.id, { heroSummary: selectedNote.heroSummary })"
              ></textarea>
            </label>

            <div class="admin-editor-help">
              <div class="admin-editor-help-head">
                <div>
                  <strong>正文写法说明</strong>
                  <span>可以直接像写文档一样输入，不需要 HTML</span>
                </div>
                <div class="admin-editor-toolbar">
                  <label class="admin-upload-button admin-upload-button-light">
                    <input type="file" accept="image/*" @change="handleEditorImageUpload">
                    上传正文图片
                  </label>
                  <label class="admin-upload-button admin-upload-button-light">
                    <input type="file" accept="video/*" @change="handleEditorVideoUpload">
                    上传正文视频
                  </label>
                </div>
              </div>
              <div class="admin-editor-help-grid">
                <p><code>空一行</code>：自动分段</p>
                <p><code>## 小标题</code>：生成二级标题</p>
                <p><code>- 列表项</code>：生成列表</p>
                <p><code>```js</code>：生成代码块</p>
                <p><code>![图片说明](地址)</code>：插入正文图片</p>
                <p><code>[视频](地址)</code>：插入视频</p>
              </div>
              <p class="admin-media-tip">
                现在可以直接上传图片和视频到本地后端，系统会自动把地址插入到当前光标位置。
                建议图片尽量控制在几百 KB 内，视频压缩后再上传，前台加载会更快。
              </p>
            </div>

            <label class="admin-field">
              <span>正文内容</span>
              <small>这里直接写文章内容即可，就像写普通文档一样。</small>
              <textarea
                ref="articleEditorRef"
                v-model="selectedNote.contentText"
                rows="18"
                class="admin-article-editor"
                @input="patchNote(selectedNote.id, { contentText: selectedNote.contentText })"
              ></textarea>
            </label>

            <div class="admin-preview-box">
              <div class="admin-preview-head">
                <div>
                  <strong>当前文章预览信息</strong>
                  <p class="admin-preview-meta">
                    当前自动统计：{{ selectedNote.codeBlocks || 0 }} 个代码块
                  </p>
                </div>
                <RouterLink :to="`/tech-notes/${selectedNote.slug}`">打开当前线上版本</RouterLink>
              </div>
              <p v-if="selectedNote.summary">{{ selectedNote.summary }}</p>
            </div>
          </div>

          <div v-if="!selectedNote" class="notes-empty-state">
            <h2>还没有选中文章</h2>
            <p>先从左侧选择一篇文章，或者点击“新增技术手记”。</p>
          </div>
        </div>
      </div>
    </section>

    <div v-if="contentDirty" class="admin-save-bar">
      <div class="admin-save-bar__meta">
        <strong>有未保存修改</strong>
        <span>现在改动还只是草稿，点击确认保存后才会同步到前台。</span>
      </div>
      <div class="admin-save-bar__actions">
        <button
          type="button"
          class="admin-secondary-button"
          :disabled="savingContent"
          @click="saveDraftContent"
        >
          {{ savingContent ? "正在保存..." : "确认保存内容" }}
        </button>
        <button
          type="button"
          class="admin-danger-button"
          :disabled="savingContent"
          @click="discardDraftChanges"
        >
          放弃修改
        </button>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, nextTick, onMounted, reactive, ref, watch, watchEffect } from "vue";
import { RouterLink } from "vue-router";
import TechNoteBlockEditor from "@/components/TechNoteBlockEditor.vue";
import { useAuthStore } from "@/store/authStore";
import { useContentStore } from "@/store/contentStore";
import { sortSoftwareLinksByName } from "@/utils/softwareSort";

const {
  currentUser,
  users,
  isSuperAdmin,
  fetchUsers,
  addUser,
  updateUser,
  removeUser,
} = useAuthStore();

const {
  ensureContentLoaded,
  loadContent,
  createContentSnapshot,
  saveContentDraft,
  uploadMediaFile,
  getDefaultContentSnapshot,
  createEmptyNavGroup,
  createEmptyNavLink,
  createEmptyTechNote,
  createEmptyLightVideo,
  createEmptyAudioTrack,
  createEmptySoftwareLink,
  normalizeContentBlocks,
  normalizeModuleIntros,
  renderContentBlocksToHtml,
  countCodeBlocksInContentBlocks,
  slugify,
  ensureUniqueSlug,
  getBilibiliEmbedUrl,
  extractBilibiliVideoParts,
  savingContent,
  contentError: storeContentError,
} = useContentStore();

const navSides = [
  { key: "left", label: "左侧导航" },
  { key: "right", label: "右侧导航" },
];

const usersLoading = ref(false);
const userActionMessage = ref("");
const userActionError = ref(false);

const contentMessage = ref("");
const contentMessageError = ref(false);
const draftNavGroups = ref({ left: [], right: [] });
const draftModuleIntros = ref(normalizeModuleIntros());
const draftTechNotes = ref([]);
const draftLightVideos = ref([]);
const draftAudioTracks = ref([]);
const draftSoftwareLinks = ref([]);
const selectedNoteId = ref(null);
const selectedVideoId = ref(null);
const selectedAudioTrackId = ref(null);
const selectedSoftwareLinkId = ref(null);
const techNoteSearchTerm = ref("");
const lightVideoSearchTerm = ref("");
const audioTrackSearchTerm = ref("");
const softwareSearchTerm = ref("");
const contentDirty = ref(false);
const articleEditorRef = ref(null);
const softwareLogoInputRef = ref(null);

const newUser = reactive({
  username: "",
  displayName: "",
  password: "",
  canAccessAdmin: true,
  isSuperAdmin: false,
});

const moduleIntroEditors = [
  { key: "techNotes", label: "技术手记", routeLabel: "/tech-notes" },
  { key: "lightVideos", label: "光影音域", routeLabel: "/light-videos" },
  { key: "soundBlank", label: "声音留白", routeLabel: "/sound-blank" },
  { key: "commonSoftware", label: "常用软件", routeLabel: "/common-software" },
];

const selectedNote = computed(
  () => draftTechNotes.value.find((item) => item.id === selectedNoteId.value) || null
);

const selectedVideo = computed(
  () => draftLightVideos.value.find((item) => item.id === selectedVideoId.value) || null
);

const selectedAudioTrack = computed(
  () =>
    draftAudioTracks.value.find((item) => item.id === selectedAudioTrackId.value) ||
    null
);

const selectedSoftwareLink = computed(
  () =>
    draftSoftwareLinks.value.find((item) => item.id === selectedSoftwareLinkId.value) ||
    null
);

const selectedVideoEmbedUrl = computed(() =>
  selectedVideo.value
    ? selectedVideo.value.embedUrl ||
      getBilibiliEmbedUrl(selectedVideo.value.sourceUrl, {
        page: selectedVideo.value.page,
      })
    : ""
);

const getPlayableMediaUrl = (mediaUrl = "") => {
  const source = String(mediaUrl || "").trim();
  if (!source || source.startsWith("/") || source.startsWith("data:")) {
    return source;
  }

  if (/^https?:\/\//i.test(source)) {
    return `/api/audio-proxy?url=${encodeURIComponent(source)}`;
  }

  return source;
};

const selectedAudioPreviewUrl = computed(() =>
  getPlayableMediaUrl(selectedAudioTrack.value?.audioUrl || "")
);

const normalizeKeyword = (value = "") =>
  String(value || "").trim().toLocaleLowerCase();

const includesKeyword = (fields = [], keyword = "") =>
  !keyword ||
  fields
    .map((field) => String(field || "").toLocaleLowerCase())
    .some((field) => field.includes(keyword));

const filteredDraftTechNotes = computed(() => {
  const keyword = normalizeKeyword(techNoteSearchTerm.value);

  return draftTechNotes.value.filter((note) =>
    includesKeyword(
      [
        note.title,
        note.categoryTitle,
        note.categorySlug,
        note.summary,
        note.heroSummary,
        note.date,
        note.reading,
        note.slug,
      ],
      keyword
    )
  );
});

const filteredDraftLightVideos = computed(() => {
  const keyword = normalizeKeyword(lightVideoSearchTerm.value);

  return draftLightVideos.value.filter((video) =>
    includesKeyword(
      [
        video.title,
        video.categoryTitle,
        video.categorySlug,
        video.summary,
        video.heroSummary,
        video.date,
        video.duration,
        video.slug,
        video.sourceUrl,
        video.bvid,
        video.aid,
      ],
      keyword
    )
  );
});

const filteredDraftAudioTracks = computed(() => {
  const keyword = normalizeKeyword(audioTrackSearchTerm.value);

  return draftAudioTracks.value.filter((track) =>
    includesKeyword(
      [
        track.title,
        track.artist,
        track.categoryTitle,
        track.categorySlug,
        track.summary,
        track.date,
        track.duration,
        track.slug,
        track.audioUrl,
        track.lyricsUrl,
      ],
      keyword
    )
  );
});

const filteredDraftSoftwareLinks = computed(() => {
  const keyword = normalizeKeyword(softwareSearchTerm.value);

  return sortSoftwareLinksByName(
    draftSoftwareLinks.value.filter((software) =>
      includesKeyword(
        [
          software.name,
          software.url,
          software.logo,
        ],
        keyword
      )
    )
  );
});

const setUserMessage = (message, isError = false) => {
  userActionMessage.value = message;
  userActionError.value = isError;
};

const setContentMessage = (message, isError = false) => {
  contentMessage.value = message;
  contentMessageError.value = isError;
};

const syncSelection = () => {
  if (!draftTechNotes.value.length) {
    selectedNoteId.value = null;
  } else {
    const exists = draftTechNotes.value.some((item) => item.id === selectedNoteId.value);
    if (!exists) {
      selectedNoteId.value = draftTechNotes.value[0].id;
    }
  }

  if (!draftLightVideos.value.length) {
    selectedVideoId.value = null;
  } else {
    const videoExists = draftLightVideos.value.some((item) => item.id === selectedVideoId.value);
    if (!videoExists) {
      selectedVideoId.value = draftLightVideos.value[0].id;
    }
  }

  if (!draftAudioTracks.value.length) {
    selectedAudioTrackId.value = null;
  } else {
    const audioExists = draftAudioTracks.value.some(
      (item) => item.id === selectedAudioTrackId.value
    );
    if (!audioExists) {
      selectedAudioTrackId.value = draftAudioTracks.value[0].id;
    }
  }

  if (!draftSoftwareLinks.value.length) {
    selectedSoftwareLinkId.value = null;
    return;
  }

  const softwareExists = draftSoftwareLinks.value.some(
    (item) => item.id === selectedSoftwareLinkId.value
  );
  if (!softwareExists) {
    selectedSoftwareLinkId.value = draftSoftwareLinks.value[0].id;
  }
};

const syncDraftFromStore = () => {
  const snapshot = createContentSnapshot();
  draftNavGroups.value = snapshot.navGroups;
  draftModuleIntros.value = normalizeModuleIntros(snapshot.moduleIntros);
  draftTechNotes.value = snapshot.techNotes;
  draftLightVideos.value = snapshot.lightVideos || [];
  draftAudioTracks.value = snapshot.audioTracks || [];
  draftSoftwareLinks.value = sortSoftwareLinksByName(snapshot.softwareLinks || []);
  contentDirty.value = false;
  syncSelection();
};

const markDirty = () => {
  contentDirty.value = true;
};

const loadUsers = async () => {
  if (!isSuperAdmin.value) {
    return;
  }

  usersLoading.value = true;
  const result = await fetchUsers();
  usersLoading.value = false;

  if (!result.ok) {
    setUserMessage(result.error, true);
  }
};

const createUser = async () => {
  usersLoading.value = true;
  const result = await addUser({
    username: newUser.username,
    displayName: newUser.displayName,
    password: newUser.password,
    canAccessAdmin: newUser.canAccessAdmin,
    isSuperAdmin: newUser.isSuperAdmin,
  });
  usersLoading.value = false;

  if (!result.ok) {
    setUserMessage(result.error, true);
    return;
  }

  newUser.username = "";
  newUser.displayName = "";
  newUser.password = "";
  newUser.canAccessAdmin = true;
  newUser.isSuperAdmin = false;
  setUserMessage("用户已创建。");
};

const saveUser = async (user, patch) => {
  usersLoading.value = true;
  const result = await updateUser(user.id, patch);
  usersLoading.value = false;

  if (!result.ok) {
    setUserMessage(result.error, true);
    return;
  }

  setUserMessage("用户信息已更新。");
};

const deleteUser = async (userId) => {
  if (!window.confirm("确认删除这个用户吗？")) {
    return;
  }

  usersLoading.value = true;
  const result = await removeUser(userId);
  usersLoading.value = false;

  if (!result.ok) {
    setUserMessage(result.error, true);
    return;
  }

  setUserMessage("用户已删除。");
};

const reloadDraftFromServer = async () => {
  const result = await loadContent();
  if (!result.ok) {
    setContentMessage(result.error, true);
    return;
  }

  syncDraftFromStore();
  setContentMessage("已从服务器重新载入最新内容。");
};

const saveDraftContent = async () => {
  const result = await saveContentDraft({
    navGroups: draftNavGroups.value,
    moduleIntros: draftModuleIntros.value,
    techNotes: draftTechNotes.value,
    lightVideos: draftLightVideos.value,
    audioTracks: draftAudioTracks.value,
    softwareLinks: draftSoftwareLinks.value,
  });

  if (!result.ok) {
    setContentMessage(result.error, true);
    return;
  }

  syncDraftFromStore();
  setContentMessage("内容已确认保存，前台现在会显示这次修改。");
};

const discardDraftChanges = () => {
  if (!contentDirty.value) {
    return;
  }

  if (!window.confirm("确认放弃当前未保存的内容草稿吗？")) {
    return;
  }

  syncDraftFromStore();
  setContentMessage("未保存的草稿修改已放弃。");
};

const resetDraftToDefault = () => {
  if (!window.confirm("确认把当前草稿恢复成默认模板吗？这一步仍需要你手动保存后才会真正生效。")) {
    return;
  }

  const snapshot = getDefaultContentSnapshot();
  draftNavGroups.value = snapshot.navGroups;
  draftModuleIntros.value = normalizeModuleIntros(snapshot.moduleIntros);
  draftTechNotes.value = snapshot.techNotes;
  draftLightVideos.value = snapshot.lightVideos || [];
  draftAudioTracks.value = snapshot.audioTracks || [];
  draftSoftwareLinks.value = sortSoftwareLinksByName(snapshot.softwareLinks || []);
  markDirty();
  syncSelection();
  setContentMessage("默认模板已载入草稿区，记得点击“确认保存内容”后才会生效。");
};

const updateDraftNavGroup = (side, groupId, patch) => {
  const group = draftNavGroups.value[side].find((item) => item.id === groupId);
  if (!group) {
    return;
  }

  Object.assign(group, patch);
  group.key = slugify(group.full || group.short || group.key);
  markDirty();
};

const addGroup = (side) => {
  draftNavGroups.value[side].push(createEmptyNavGroup(side));
  markDirty();
};

const removeGroup = (side, groupId) => {
  if (!window.confirm("确认删除这个导航组吗？")) {
    return;
  }

  const index = draftNavGroups.value[side].findIndex((item) => item.id === groupId);
  if (index >= 0) {
    draftNavGroups.value[side].splice(index, 1);
    markDirty();
  }
};

const addLink = (side, groupId) => {
  const group = draftNavGroups.value[side].find((item) => item.id === groupId);
  if (!group) {
    return;
  }

  group.links.push(createEmptyNavLink());
  markDirty();
};

const updateDraftNavLink = (side, groupId, linkId, patch) => {
  const group = draftNavGroups.value[side].find((item) => item.id === groupId);
  const link = group?.links.find((item) => item.id === linkId);
  if (!link) {
    return;
  }

  Object.assign(link, patch);
  markDirty();
};

const moveNavLink = (side, groupId, linkId, direction) => {
  const group = draftNavGroups.value[side].find((item) => item.id === groupId);
  if (!group?.links?.length) {
    return;
  }

  const currentIndex = group.links.findIndex((item) => item.id === linkId);
  const nextIndex = currentIndex + direction;
  if (currentIndex < 0 || nextIndex < 0 || nextIndex >= group.links.length) {
    return;
  }

  const [link] = group.links.splice(currentIndex, 1);
  group.links.splice(nextIndex, 0, link);
  markDirty();
};

const removeLink = (side, groupId, linkId) => {
  if (!window.confirm("确认删除这个链接吗？")) {
    return;
  }

  const group = draftNavGroups.value[side].find((item) => item.id === groupId);
  if (!group) {
    return;
  }

  const index = group.links.findIndex((item) => item.id === linkId);
  if (index >= 0) {
    group.links.splice(index, 1);
    markDirty();
  }
};

const patchModuleIntro = (moduleKey) => {
  draftModuleIntros.value = normalizeModuleIntros(draftModuleIntros.value);
  if (!draftModuleIntros.value[moduleKey]) {
    return;
  }

  markDirty();
};

const splitIntroTags = (value = "") =>
  String(value || "")
    .split(/[\n,，、]+/)
    .map((item) => item.trim())
    .filter(Boolean)
    .slice(0, 8);

const patchModuleIntroTags = (moduleKey, value) => {
  if (!draftModuleIntros.value[moduleKey]) {
    return;
  }

  draftModuleIntros.value[moduleKey].tags = splitIntroTags(value);
  draftModuleIntros.value = normalizeModuleIntros(draftModuleIntros.value);
  markDirty();
};

const createNote = () => {
  const note = createEmptyTechNote(draftTechNotes.value);
  draftTechNotes.value.unshift(note);
  selectedNoteId.value = note.id;
  markDirty();
  setContentMessage("新文章已经加入草稿区，确认保存后才会发布到前台。");
};

const patchNote = (noteId, patch) => {
  const note = draftTechNotes.value.find((item) => item.id === noteId);
  if (!note) {
    return;
  }

  const hasContentHtmlPatch = Object.prototype.hasOwnProperty.call(patch, "contentHtml");
  const hasContentBlocksPatch = Object.prototype.hasOwnProperty.call(patch, "contentBlocks");
  const hasCodeBlocksPatch = Object.prototype.hasOwnProperty.call(patch, "codeBlocks");

  Object.assign(note, patch);
  note.categorySlug = makeAutoSlug(note.categoryTitle, note.categorySlug || "tech-note");
  note.slug = ensureUniqueSlug(note.slug || note.title, draftTechNotes.value, note.id);

  if (hasContentBlocksPatch) {
    note.contentBlocks = normalizeContentBlocks(note.contentBlocks);
  }

  if (hasContentHtmlPatch) {
    note.contentHtml = String(patch.contentHtml || "");
  } else if (hasContentBlocksPatch && !String(note.contentHtml || "").trim()) {
    note.contentHtml = renderContentBlocksToHtml(note.contentBlocks);
  }

  if (hasCodeBlocksPatch) {
    note.codeBlocks = Number(patch.codeBlocks || 0);
  } else if (hasContentBlocksPatch) {
    note.codeBlocks = countCodeBlocksInContentBlocks(note.contentBlocks);
  }

  markDirty();
};

const removeNote = (noteId) => {
  if (!window.confirm("确认删除这篇技术手记吗？")) {
    return;
  }

  const index = draftTechNotes.value.findIndex((item) => item.id === noteId);
  if (index >= 0) {
    draftTechNotes.value.splice(index, 1);
    syncSelection();
    markDirty();
    setContentMessage("文章已从草稿区删除，保存后才会真正从前台移除。");
  }
};

const createVideo = () => {
  const video = createEmptyLightVideo(draftLightVideos.value);
  draftLightVideos.value.unshift(video);
  selectedVideoId.value = video.id;
  markDirty();
  setContentMessage("新视频已经加入光影音域草稿区，确认保存后才会发布到前台。");
};

function makeAutoSlug(value = "", fallback = "item") {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return normalized || fallback;
}

function getAutoLightVideoSlug(video, parts) {
  const sourceKey = parts.bvid || (parts.aid ? `av-${parts.aid}` : "");
  return makeAutoSlug(
    sourceKey || video.title,
    video.slug || `light-video-${Date.now().toString(36)}`
  );
}

function getAutoLightVideoCategorySlug(video) {
  return makeAutoSlug(video.categoryTitle, video.categorySlug || "video-work");
}

const patchVideo = (videoId, patch) => {
  const video = draftLightVideos.value.find((item) => item.id === videoId);
  if (!video) {
    return;
  }

  Object.assign(video, patch);

  const parts = extractBilibiliVideoParts(video.sourceUrl);
  const page = Math.max(1, Number.parseInt(parts.page || "1", 10) || 1);
  video.categorySlug = getAutoLightVideoCategorySlug(video);
  video.slug = ensureUniqueSlug(
    getAutoLightVideoSlug(video, parts),
    draftLightVideos.value,
    video.id
  );
  video.page = page;
  video.bvid = parts.bvid;
  video.aid = parts.aid;
  video.summary = video.heroSummary || video.summary || "";
  video.embedUrl = getBilibiliEmbedUrl(video.sourceUrl, {
    page,
  });

  markDirty();
};

const removeVideo = (videoId) => {
  if (!window.confirm("确认删除这个光影音域视频吗？")) {
    return;
  }

  const index = draftLightVideos.value.findIndex((item) => item.id === videoId);
  if (index >= 0) {
    draftLightVideos.value.splice(index, 1);
    syncSelection();
    markDirty();
    setContentMessage("视频已从草稿区删除，保存后才会真正从前台移除。");
  }
};

const createAudioTrack = () => {
  const track = createEmptyAudioTrack(draftAudioTracks.value);
  draftAudioTracks.value.unshift(track);
  selectedAudioTrackId.value = track.id;
  markDirty();
  setContentMessage("新歌曲已经加入声音留白草稿区，确认保存后才会发布到前台。");
};

function getAutoAudioTrackCategorySlug(track) {
  return makeAutoSlug(track.categoryTitle, track.categorySlug || "music");
}

const patchAudioTrack = (trackId, patch) => {
  const track = draftAudioTracks.value.find((item) => item.id === trackId);
  if (!track) {
    return;
  }

  Object.assign(track, patch);
  track.categorySlug = getAutoAudioTrackCategorySlug(track);
  track.slug = ensureUniqueSlug(
    makeAutoSlug(track.title, track.slug || `audio-track-${Date.now().toString(36)}`),
    draftAudioTracks.value,
    track.id
  );

  markDirty();
};

const removeAudioTrack = (trackId) => {
  if (!window.confirm("确认删除这首声音留白歌曲吗？")) {
    return;
  }

  const index = draftAudioTracks.value.findIndex((item) => item.id === trackId);
  if (index >= 0) {
    draftAudioTracks.value.splice(index, 1);
    syncSelection();
    markDirty();
    setContentMessage("歌曲已从草稿区删除，保存后才会真正从前台移除。");
  }
};

const handleAudioLyricsUpload = async (event) => {
  const file = event.target?.files?.[0];
  if (!file || !selectedAudioTrack.value) {
    return;
  }

  const result = await uploadMediaFile(file);
  if (!result.ok) {
    setContentMessage(result.error, true);
    event.target.value = "";
    return;
  }

  patchAudioTrack(selectedAudioTrack.value.id, {
    lyricsUrl: result.data.url,
  });
  setContentMessage("歌词文件已上传并关联到当前歌曲，记得确认保存内容。");
  event.target.value = "";
};

const clearAudioLyrics = () => {
  if (!selectedAudioTrack.value) {
    return;
  }

  patchAudioTrack(selectedAudioTrack.value.id, {
    lyricsUrl: "",
  });
  setContentMessage("歌词关联已从草稿中清除，保存后才会同步到前台。");
};

const createSoftwareLink = () => {
  const software = createEmptySoftwareLink(draftSoftwareLinks.value);
  draftSoftwareLinks.value = sortSoftwareLinksByName([software, ...draftSoftwareLinks.value]);
  selectedSoftwareLinkId.value = software.id;
  markDirty();
  setContentMessage("新软件已经加入常用软件草稿区，确认保存后才会发布到前台。");
};

const triggerSoftwareLogoUpload = () => {
  softwareLogoInputRef.value?.click();
};

const patchSoftwareLink = (softwareId, patch) => {
  const software = draftSoftwareLinks.value.find((item) => item.id === softwareId);
  if (!software) {
    return;
  }

  Object.assign(software, patch);
  draftSoftwareLinks.value = sortSoftwareLinksByName(draftSoftwareLinks.value);
  markDirty();
};

const removeSoftwareLink = (softwareId) => {
  if (!window.confirm("确认删除这个常用软件吗？")) {
    return;
  }

  const index = draftSoftwareLinks.value.findIndex((item) => item.id === softwareId);
  if (index >= 0) {
    draftSoftwareLinks.value.splice(index, 1);
    syncSelection();
    markDirty();
    setContentMessage("软件已从草稿区删除，保存后才会真正从前台移除。");
  }
};

const handleSoftwareLogoUpload = async (event) => {
  const file = event.target?.files?.[0];
  if (!file || !selectedSoftwareLink.value) {
    return;
  }

  const result = await uploadMediaFile(file);
  if (!result.ok) {
    setContentMessage(result.error, true);
    event.target.value = "";
    return;
  }

  patchSoftwareLink(selectedSoftwareLink.value.id, {
    logo: result.data.url,
  });
  setContentMessage("软件 Logo 已上传并关联到当前软件，记得确认保存内容。");
  event.target.value = "";
};

const clearSoftwareLogo = () => {
  if (!selectedSoftwareLink.value) {
    return;
  }

  patchSoftwareLink(selectedSoftwareLink.value.id, {
    logo: "",
  });
  setContentMessage("软件 Logo 已从草稿中清除，保存后才会同步到前台。");
};

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("读取图片失败"));
    reader.readAsDataURL(file);
  });

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("加载图片失败"));
    image.src = src;
  });

const optimizeImageFile = async (file) => {
  const rawDataUrl = await readFileAsDataUrl(file);

  if (file.type === "image/svg+xml") {
    return rawDataUrl;
  }

  const image = await loadImage(rawDataUrl);
  const maxWidth = 1600;
  const scale = Math.min(1, maxWidth / image.naturalWidth);
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));

  const context = canvas.getContext("2d");
  if (!context) {
    return rawDataUrl;
  }

  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/webp", 0.86);
};

const handleCoverUpload = async (event) => {
  const file = event.target?.files?.[0];
  if (!file || !selectedNote.value) {
    return;
  }

  try {
    const optimizedImage = await optimizeImageFile(file);
    patchNote(selectedNote.value.id, { image: optimizedImage });
    setContentMessage("封面图已加入草稿，并已自动压缩。记得保存后前台才会更新。");
  } catch (error) {
    setContentMessage("封面图上传失败，请换一张图片再试。", true);
  } finally {
    event.target.value = "";
  }
};

const resetCoverImage = () => {
  if (!selectedNote.value) {
    return;
  }

  patchNote(selectedNote.value.id, { image: "/images/portal-info.webp" });
  setContentMessage("默认封面图已恢复到草稿区。");
};

const getMediaLabel = (file) =>
  String(file?.name || "媒体素材")
    .replace(/\.[^.]+$/, "")
    .replace(/[-_]+/g, " ")
    .trim() || "媒体素材";

const insertSnippetAtCursor = async (snippet) => {
  if (!selectedNote.value) {
    return;
  }

  const textarea = articleEditorRef.value;
  const currentText = String(selectedNote.value.contentText || "");
  const start = textarea?.selectionStart ?? currentText.length;
  const end = textarea?.selectionEnd ?? currentText.length;
  const before = currentText.slice(0, start);
  const after = currentText.slice(end);
  const leading = before && !before.endsWith("\n\n") ? "\n\n" : "";
  const trailing = after && !after.startsWith("\n\n") ? "\n\n" : "";
  const nextText = `${before}${leading}${snippet}${trailing}${after}`;
  const cursor = `${before}${leading}${snippet}`.length;

  patchNote(selectedNote.value.id, { contentText: nextText });

  await nextTick();
  if (textarea) {
    textarea.focus();
    textarea.setSelectionRange(cursor, cursor);
  }
};

const handleEditorMediaUpload = async (event, expectedType) => {
  const file = event.target?.files?.[0];
  if (!file || !selectedNote.value) {
    return;
  }

  const result = await uploadMediaFile(file);
  if (!result.ok) {
    setContentMessage(result.error, true);
    event.target.value = "";
    return;
  }

  const mediaType = result.data.type || expectedType;
  const snippet =
    mediaType === "video"
      ? `[视频](${result.data.url})`
      : `![${getMediaLabel(file)}](${result.data.url})`;

  await insertSnippetAtCursor(snippet);
  setContentMessage(
    mediaType === "video"
      ? "视频已上传并插入正文草稿，记得确认保存内容。"
      : "图片已上传并插入正文草稿，记得确认保存内容。"
  );
  event.target.value = "";
};

const handleEditorImageUpload = async (event) => {
  await handleEditorMediaUpload(event, "image");
};

const handleEditorVideoUpload = async (event) => {
  await handleEditorMediaUpload(event, "video");
};

watch(
  draftTechNotes,
  () => {
    syncSelection();
  },
  { deep: true }
);

watch(
  draftLightVideos,
  () => {
    syncSelection();
  },
  { deep: true }
);

watch(
  draftAudioTracks,
  () => {
    syncSelection();
  },
  { deep: true }
);

watch(
  draftSoftwareLinks,
  () => {
    syncSelection();
  },
  { deep: true }
);

onMounted(async () => {
  const result = await ensureContentLoaded();
  if (result?.ok === false && result?.error) {
    setContentMessage(result.error, true);
  }

  syncDraftFromStore();
  loadUsers();
});

watchEffect(() => {
  document.title = "圣域空间 | 后台管理";
});
</script>
